import { useState, useEffect, useMemo } from 'react'
import {
  Dialog, DialogTitle, DialogContent, Typography, IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useAppActions } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { pmExtractQuestions, pmImportProject } from '../../services/ProjectManagerService'
import { addPendingTask, addTaskProgressListener, fetchTasks, kickTaskstream } from '../../services/TaskManagerService'
import { ICON_MD } from '../../common/theme'
import BiqUploadPhase from '../../comps/BiqUploadPhase'
import BiqReviewPhase, { type FlatItem } from '../../comps/BiqReviewPhase'
import BiqOrganizePhase, { type SectionMeta } from '../../comps/BiqOrganizePhase'
import type { ExtractedQuestion, ExtractQuestionsWebResult } from '../../types'

interface BidImportQuestionsModalProps {
  open: boolean
  projectsha: string
  onClose: () => void
  onImported: () => void
}

// Three-phase wizard for importing bid questions:
//   1. BiqUploadPhase   - file or text picker
//   2. BiqReviewPhase   - thumbnail + per-question check, merge, include/exclude
//   3. BiqOrganizePhase - full-width drag/drop with sections, then the import POST
// The modal owns the shared model (questions / items / sections) so it
// survives transitions between phases; each phase component owns its own
// phase-local state.
export default function BidImportQuestionsModal({ open, projectsha, onClose, onImported }: BidImportQuestionsModalProps) {
  const { updateQuestion } = useAppActions()

  const [phase, setPhase] = useState<'upload' | 'review' | 'organize'>('upload')

  const [file, setFile] = useState<File | null>(null)
  const [projectText, setProjectText] = useState('')
  const [isExtracting, setIsExtracting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [extractStatus, setExtractStatus] = useState('')

  const [extractResult, setExtractResult] = useState<ExtractQuestionsWebResult | null>(null)
  const [questions, setQuestions] = useState<ExtractedQuestion[]>([])
  const [items, setItems] = useState<FlatItem[]>([])
  const [sections, setSections] = useState<SectionMeta[]>([])

  // Reset on close/open so a re-opened modal starts fresh.
  useEffect(() => {
    if (!open) return
    setPhase('upload')
    setFile(null)
    setProjectText('')
    setExtractResult(null)
    setQuestions([])
    setItems([])
    setSections([])
    setIsExtracting(false)
    setIsUploading(false)
    setExtractStatus('')
  }, [open])

  // Live extraction progress: the server pushes QuestionProgressEvent
  // frames with the extraction tasksha but no questionsha. We subscribe
  // for the duration of the extraction so the latest "Extracting N%"
  // text reaches the upload phase. The text-only paste path doesn't
  // hit the extractor so this stays idle in that case.
  useEffect(() => {
    if (!isExtracting) return
    setExtractStatus('Starting…')
    const unsub = addTaskProgressListener(ev => {
      if (ev.text) setExtractStatus(ev.text)
    })
    return unsub
  }, [isExtracting])

  const qMap = useMemo(
    () => Object.fromEntries(questions.map(q => [q.questionsha, q])),
    [questions],
  )
  const acceptedCount = useMemo(
    () => questions.filter(q => q.include).length,
    [questions],
  )

  const handleFilePick = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return
    setFile(incoming[0])
  }

  const handleExtract = async () => {
    if (!file && !projectText.trim()) {
      addToast('warn', 'Please upload a file or paste text')
      return
    }
    if (!file && projectText.trim()) {
      // Text-only path: skip extract; go straight to import.
      setIsUploading(true)
      try {
        const r = await pmImportProject({ projectsha, projecttext: projectText })
        if (r?.success) {
          addToast('success', 'Imported Project')
          onImported()
          onClose()
        } else {
          addToast('error', r?.error || 'Error importing project')
        }
      } catch {
        addToast('error', 'Error importing project')
      } finally {
        setIsUploading(false)
      }
      return
    }
    if (!file) return
    setIsExtracting(true)
    // Extraction runs as a background task on the server. Wake the SSE
    // consumer up front so any progress events the backend emits during
    // the call reach the global UI indicators (task count, tkinit) in
    // real time. Same pattern as startGenerateQuestions.
    kickTaskstream()
    try {
      const result = await pmExtractQuestions({ file, projectsha })
      if (result?.success) {
        // If the backend registered a server-side task for the extract,
        // hook it into the tkinit poller so it shows up in the global
        // task list and clears its row on completion.
        if (result.tasksha) {
          addPendingTask(result.tasksha, addToast, updateQuestion)
          fetchTasks([result.tasksha], addToast, updateQuestion)
        }
        setExtractResult(result)
        const allQs: ExtractedQuestion[] = []
        for (const p of result.docpages) {
          for (const q of p.questions) allQs.push(q)
        }
        setQuestions(allQs)
        setItems(allQs.map(q => ({ type: 'question' as const, sha: q.questionsha })))
        setSections([])
        setPhase('review')
        addToast('success', 'Questions Extracted')
      } else {
        addToast('error', result?.msg || 'Error Extracting Questions')
      }
    } catch {
      addToast('error', 'Error Extracting Questions')
    } finally {
      setIsExtracting(false)
    }
  }

  const handleImport = async () => {
    if (acceptedCount === 0 && !projectText.trim()) {
      addToast('warn', 'No questions to import')
      return
    }

    // Build the ordered, sectioned list the API expects: walk `items`,
    // tracking the current section name as we cross headers.
    let currentSection: string | null = null
    const ordered: ExtractedQuestion[] = []
    for (const item of items) {
      if (item.type === 'section') {
        currentSection = sections.find(s => s.id === item.sectionId)?.name ?? null
      } else {
        const q = qMap[item.sha]
        if (q && q.include) ordered.push({ ...q, section: currentSection })
      }
    }

    setIsUploading(true)
    try {
      const r = await pmImportProject({
        projectsha,
        projecttext: projectText.trim() || undefined,
        questions: ordered,
      })
      if (r?.success) {
        addToast('success', 'Imported Project')
        onImported()
        onClose()
      } else {
        addToast('error', r?.error || 'Error importing project')
      }
    } catch {
      addToast('error', 'Error importing project')
    } finally {
      setIsUploading(false)
    }
  }

  const wide = phase !== 'upload'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ 'data-testid': 'biq_dlg', sx: { width: wide ? '95vw' : 600, height: wide ? '90vh' : 'auto', maxWidth: '95vw' } } as any}
    >
      <DialogTitle sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
        <Typography data-testid="biq_title" sx={{ fontWeight: 600, fontSize: '1rem', flex: 1 }}>
          Import Bid Questions
        </Typography>
        <IconButton size="small" data-testid="biq_close_btn" onClick={onClose}>
          <CloseIcon sx={{ fontSize: ICON_MD }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

        {phase === 'upload' && (
          <BiqUploadPhase
            file={file}
            setFile={setFile}
            projectText={projectText}
            setProjectText={setProjectText}
            isExtracting={isExtracting}
            isUploading={isUploading}
            extractStatus={extractStatus}
            onExtract={handleExtract}
            onCancel={onClose}
            handleFilePick={handleFilePick}
          />
        )}

        {phase === 'review' && extractResult && (
          <BiqReviewPhase
            extractResult={extractResult}
            questions={questions}
            setQuestions={setQuestions}
            items={items}
            setItems={setItems}
            qMap={qMap}
            acceptedCount={acceptedCount}
            isUploading={isUploading}
            onNext={() => setPhase('organize')}
            onCancel={onClose}
          />
        )}

        {phase === 'organize' && (
          <BiqOrganizePhase
            items={items}
            setItems={setItems}
            sections={sections}
            setSections={setSections}
            // questions={questions}
            setQuestions={setQuestions}
            qMap={qMap}
            acceptedCount={acceptedCount}
            isUploading={isUploading}
            onBack={() => setPhase('review')}
            onCancel={onClose}
            onImport={handleImport}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
